import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Info as InfoIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import api from '../../utils/api';

const PlanFreezeApproval = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [pendingFreezes, setPendingFreezes] = useState([]);
  const [activeFreezes, setActiveFreezes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionDialog, setActionDialog] = useState({
    open: false,
    type: null, // 'approve' or 'reject'
    freeze: null
  });
  const [actionData, setActionData] = useState({
    rejectionReason: '',
    adminNotes: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      if (activeTab === 0) {
        await loadPendingFreezes();
      } else {
        await loadActiveFreezes();
      }
    } finally {
      setLoading(false);
    }
  }, [activeTab]);

  useEffect(() => {
    loadData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const loadPendingFreezes = async () => {
    try {
      const response = await api.get('/admin/plan-freeze/pending');
      setPendingFreezes(response.data.data);
    } catch (error) {
      console.error('Error loading pending freezes:', error);
    }
  };

  const loadActiveFreezes = async () => {
    try {
      const response = await api.get('/admin/plan-freeze/active');
      setActiveFreezes(response.data.data);
    } catch (error) {
      console.error('Error loading active freezes:', error);
    }
  };

  const openActionDialog = (type, freeze) => {
    setActionDialog({ open: true, type, freeze });
    setActionData({ rejectionReason: '', adminNotes: '' });
  };

  const closeActionDialog = () => {
    setActionDialog({ open: false, type: null, freeze: null });
    setActionData({ rejectionReason: '', adminNotes: '' });
  };

  const handleApprove = async () => {
    if (!actionDialog.freeze) return;

    setSubmitting(true);
    try {
      await api.post(`/admin/plan-freeze/${actionDialog.freeze._id}/approve`, {
        adminNotes: actionData.adminNotes
      });

      await loadData();
      closeActionDialog();
    } catch (error) {
      console.error('Error approving freeze:', error);
      alert(error.response?.data?.error || 'Failed to approve freeze request');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReject = async () => {
    if (!actionDialog.freeze || !actionData.rejectionReason.trim()) {
      alert('Rejection reason is required');
      return;
    }

    setSubmitting(true);
    try {
      await api.post(`/admin/plan-freeze/${actionDialog.freeze._id}/reject`, {
        rejectionReason: actionData.rejectionReason,
        adminNotes: actionData.adminNotes
      });

      await loadData();
      closeActionDialog();
    } catch (error) {
      console.error('Error rejecting freeze:', error);
      alert(error.response?.data?.error || 'Failed to reject freeze request');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('tr-TR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const calculateImpact = (freeze) => {
    const currentEnd = new Date(freeze.user?.membershipEndDate);
    const freezeDays = freeze.requestedDays;
    const newEnd = new Date(currentEnd.getTime() + (freezeDays * 24 * 60 * 60 * 1000));

    return {
      currentEndDate: currentEnd,
      newEndDate: newEnd,
      hasNextPlan: freeze.hadNextPlan
    };
  };

  const getDaysRemaining = (endDate) => {
    if (!endDate) return 0;
    const now = new Date();
    const end = new Date(endDate);
    const diffTime = end - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(0, diffDays);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5">
          Plan Freeze Management
        </Typography>
        <IconButton onClick={loadData} disabled={loading}>
          <RefreshIcon />
        </IconButton>
      </Box>

      <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)} sx={{ mb: 3 }}>
        <Tab label={`Pending (${pendingFreezes.length})`} />
        <Tab label={`Active (${activeFreezes.length})`} />
      </Tabs>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Pending Freezes Tab */}
          {activeTab === 0 && (
            <>
              {pendingFreezes.length === 0 ? (
                <Alert severity="info">No pending freeze requests</Alert>
              ) : (
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>User</TableCell>
                        <TableCell>Plan</TableCell>
                        <TableCell align="center">Days Requested</TableCell>
                        <TableCell align="center">Requested Date</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {pendingFreezes.map((freeze) => (
                        <TableRow key={freeze._id} hover>
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium">
                              {freeze.user?.firstName} {freeze.user?.lastName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {freeze.user?.email}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {freeze.plan?.name}
                          </TableCell>
                          <TableCell align="center">
                            <Chip label={`${freeze.requestedDays} days`} size="small" />
                          </TableCell>
                          <TableCell align="center">
                            {formatDate(freeze.requestedAt)}
                          </TableCell>
                          <TableCell align="right">
                            <Tooltip title="View Details & Approve">
                              <IconButton
                                color="success"
                                onClick={() => openActionDialog('approve', freeze)}
                                size="small"
                              >
                                <ApproveIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Reject">
                              <IconButton
                                color="error"
                                onClick={() => openActionDialog('reject', freeze)}
                                size="small"
                              >
                                <RejectIcon />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </>
          )}

          {/* Active Freezes Tab */}
          {activeTab === 1 && (
            <>
              {activeFreezes.length === 0 ? (
                <Alert severity="info">No active freezes</Alert>
              ) : (
                <Grid container spacing={2}>
                  {activeFreezes.map((freeze) => (
                    <Grid item xs={12} md={6} key={freeze._id}>
                      <Card>
                        <CardContent>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                            <Typography variant="subtitle1" fontWeight="medium">
                              {freeze.user?.firstName} {freeze.user?.lastName}
                            </Typography>
                            <Chip label="Active" color="info" size="small" />
                          </Box>

                          <Grid container spacing={2}>
                            <Grid item xs={6}>
                              <Typography variant="caption" color="text.secondary">
                                Plan
                              </Typography>
                              <Typography variant="body2">
                                {freeze.plan?.name}
                              </Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="caption" color="text.secondary">
                                Days Remaining
                              </Typography>
                              <Typography variant="body2" color="primary.main">
                                {getDaysRemaining(freeze.freezeEndDate)} days
                              </Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="caption" color="text.secondary">
                                Freeze Start
                              </Typography>
                              <Typography variant="body2">
                                {formatDate(freeze.freezeStartDate)}
                              </Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="caption" color="text.secondary">
                                Freeze End
                              </Typography>
                              <Typography variant="body2">
                                {formatDate(freeze.freezeEndDate)}
                              </Typography>
                            </Grid>
                          </Grid>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              )}
            </>
          )}
        </>
      )}

      {/* Action Dialog */}
      <Dialog
        open={actionDialog.open}
        onClose={closeActionDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {actionDialog.type === 'approve' ? 'Approve' : 'Reject'} Freeze Request
        </DialogTitle>
        <DialogContent>
          {actionDialog.freeze && (
            <>
              {/* User Info */}
              <Card sx={{ mb: 3, bgcolor: 'grey.50' }}>
                <CardContent>
                  <Typography variant="subtitle2" gutterBottom>
                    User Information
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">
                        Name
                      </Typography>
                      <Typography variant="body2">
                        {actionDialog.freeze.user?.firstName} {actionDialog.freeze.user?.lastName}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">
                        Email
                      </Typography>
                      <Typography variant="body2">
                        {actionDialog.freeze.user?.email}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Request Details */}
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="subtitle2" gutterBottom>
                    Request Details
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">
                        Plan
                      </Typography>
                      <Typography variant="body2">
                        {actionDialog.freeze.plan?.name}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">
                        Freeze Duration
                      </Typography>
                      <Typography variant="body2">
                        {actionDialog.freeze.requestedDays} days
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary">
                        Reason
                      </Typography>
                      <Typography variant="body2">
                        {actionDialog.freeze.requestReason || 'No reason provided'}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Impact Preview */}
              {actionDialog.type === 'approve' && (
                <Card sx={{ mb: 3 }}>
                  <CardContent>
                    <Typography variant="subtitle2" gutterBottom>
                      Impact Preview
                    </Typography>
                    {(() => {
                      const impact = calculateImpact(actionDialog.freeze);
                      return (
                        <Grid container spacing={2}>
                          <Grid item xs={6}>
                            <Typography variant="caption" color="text.secondary">
                              Current End Date
                            </Typography>
                            <Typography variant="body2">
                              {formatDate(impact.currentEndDate)}
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="caption" color="text.secondary">
                              New End Date
                            </Typography>
                            <Typography variant="body2" color="primary.main" fontWeight="medium">
                              {formatDate(impact.newEndDate)}
                            </Typography>
                          </Grid>
                          {impact.hasNextPlan && (
                            <Grid item xs={12}>
                              <Alert severity="info" icon={<InfoIcon />}>
                                User has a next plan queued. Its start date will be adjusted automatically.
                              </Alert>
                            </Grid>
                          )}
                        </Grid>
                      );
                    })()}
                  </CardContent>
                </Card>
              )}

              {/* Rejection Reason (for reject action) */}
              {actionDialog.type === 'reject' && (
                <TextField
                  fullWidth
                  label="Rejection Reason *"
                  multiline
                  rows={3}
                  value={actionData.rejectionReason}
                  onChange={(e) => setActionData({ ...actionData, rejectionReason: e.target.value })}
                  sx={{ mb: 2 }}
                  required
                />
              )}

              {/* Admin Notes (optional) */}
              <TextField
                fullWidth
                label="Admin Notes (Optional)"
                multiline
                rows={2}
                value={actionData.adminNotes}
                onChange={(e) => setActionData({ ...actionData, adminNotes: e.target.value })}
                helperText="Internal notes (not visible to user)"
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeActionDialog} disabled={submitting}>
            Cancel
          </Button>
          {actionDialog.type === 'approve' ? (
            <Button
              onClick={handleApprove}
              variant="contained"
              color="success"
              disabled={submitting}
              startIcon={submitting ? <CircularProgress size={20} /> : <ApproveIcon />}
            >
              {submitting ? 'Approving...' : 'Approve Freeze'}
            </Button>
          ) : (
            <Button
              onClick={handleReject}
              variant="contained"
              color="error"
              disabled={submitting || !actionData.rejectionReason.trim()}
              startIcon={submitting ? <CircularProgress size={20} /> : <RejectIcon />}
            >
              {submitting ? 'Rejecting...' : 'Reject Request'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PlanFreezeApproval;
